import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Camera, Play, X, Maximize2, Pause, Lock } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, GALLERY_MEDIA } from "@/lib/api";

const isVideoSrc = (src = "") => /\.(mov|mp4|webm|m4v)(\?|$)/i.test(src);

export default function Gallery() {
  const [adminMedia, setAdminMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openIdx, setOpenIdx] = useState(null);

  useEffect(() => {
    api
      .get("/gallery")
      .then((r) => setAdminMedia(r.data || []))
      .catch(() => setAdminMedia([]))
      .finally(() => setLoading(false));
  }, []);

  const adminAsMedia = adminMedia.map((img) => ({
    id: img.id,
    type: isVideoSrc(img.image_data) ? "video" : "image",
    src: img.image_data,
  }));
  const items = [...adminAsMedia, ...GALLERY_MEDIA];
  const open = items[openIdx];

  return (
    <section
      id="galerie"
      data-testid="gallery-section"
      className="bg-white py-20 lg:py-28"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="reveal flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div className="max-w-2xl">
            <p className="text-sm uppercase tracking-[0.18em] text-[#1E88C8] font-semibold">
              Galerie
            </p>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-[#0B3A5B] mt-3">
              Avant / Après en vidéo
            </h2>
            <p className="mt-4 text-base text-[#2F3438]/75 leading-relaxed">
              Découvrez le résultat des prestations à travers des vidéos réelles
              montrant le niveau de finition obtenu sur les véhicules confiés.
            </p>
          </div>
          <Link
            to="/admin"
            data-testid="gallery-admin-link"
            className="shrink-0 inline-flex items-center gap-2 text-sm text-[#0B3A5B]/70 hover:text-[#0B3A5B] transition-colors"
          >
            <Lock className="h-3.5 w-3.5" />
            Espace admin
          </Link>
        </div>

        {loading ? (
          <BentoSkeleton />
        ) : items.length === 0 ? (
          <div className="mt-12 text-center py-20 border border-dashed border-[#0B3A5B]/15 rounded-2xl text-[#2F3438]/60">
            Bientôt de nouvelles vidéos…
          </div>
        ) : (
          <div
            data-testid="gallery-grid"
            className="mt-12 sm:mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-7"
          >
            {items.map((m, idx) => (
              <BentoTile
                key={m.id}
                media={m}
                index={idx}
                onOpen={() => setOpenIdx(idx)}
                featured={idx === 0 && items.length >= 3}
              />
            ))}
          </div>
        )}
      </div>

      <Lightbox open={openIdx !== null} onClose={() => setOpenIdx(null)} media={open} />
    </section>
  );
}

function BentoTile({ media, index, onOpen, featured }) {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const handleMouseEnter = () => {
    const v = videoRef.current;
    if (!v) return;
    v.play().then(() => setPlaying(true)).catch(() => {});
  };
  const handleMouseLeave = () => {
    const v = videoRef.current;
    if (!v) return;
    v.pause();
    v.currentTime = 0;
    setPlaying(false);
  };

  const togglePreview = (e) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().then(() => setPlaying(true)).catch(() => {});
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  // Featured (first) tile spans 2 columns on sm+ and 2 rows on lg
  const tileSize = featured
    ? "sm:col-span-2 lg:row-span-2 aspect-[4/5] sm:aspect-[16/10] lg:aspect-auto lg:h-full"
    : "aspect-[3/4]";

  return (
    <div
      role="button"
      tabIndex={0}
      data-testid={`gallery-item-${index}`}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`group relative overflow-hidden rounded-[28px] bg-[#0B3A5B] text-left ring-1 ring-[#1E88C8]/20 hover:ring-[#1E88C8]/60 focus:outline-none focus:ring-2 focus:ring-[#1E88C8] transition-all duration-500 hover:shadow-[0_40px_80px_-30px_rgba(11,58,91,0.6),0_0_60px_-20px_rgba(30,136,200,0.3)] hover:-translate-y-1.5 shadow-[0_24px_50px_-28px_rgba(11,58,91,0.45)] cursor-pointer ${tileSize}`}
    >
      {media.type === "video" ? (
        <>
          <video
            ref={videoRef}
            src={media.src}
            muted
            loop
            playsInline
            preload="metadata"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110"
          />
          {/* Subtle premium overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B3A5B]/55 via-transparent to-transparent transition-opacity duration-500 group-hover:opacity-30" />
          <div className="absolute inset-0 ring-1 ring-inset ring-white/5 rounded-[28px] pointer-events-none" />

          {/* Center play indicator — disappears while playing */}
          <span
            className={`absolute inset-0 flex items-center justify-center transition-all duration-400 ${
              playing ? "opacity-0 scale-90" : "opacity-100"
            } group-hover:opacity-0`}
            aria-hidden
          >
            <span className="h-20 w-20 rounded-full bg-white/95 text-[#0B3A5B] flex items-center justify-center shadow-2xl shadow-black/40 backdrop-blur ring-4 ring-white/40">
              <Play className="h-7 w-7 fill-current translate-x-0.5" />
            </span>
          </span>

          {/* Discreet "Vidéo" pill */}
          <span className="absolute top-4 right-4 px-2.5 py-1 rounded-full bg-[#1E88C8] text-white text-[10px] font-bold uppercase tracking-wider shadow-lg shadow-[#1E88C8]/40">
            Vidéo
          </span>

          {/* Subtle play/pause toggle */}
          <button
            type="button"
            onClick={togglePreview}
            data-testid={`gallery-play-${index}`}
            aria-label={playing ? "Pause" : "Aperçu"}
            className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-white/95 text-[#0B3A5B] text-xs font-semibold backdrop-blur shadow-sm transition-transform hover:scale-105"
          >
            {playing ? <Pause className="h-3 w-3 fill-current" /> : <Play className="h-3 w-3 fill-current" />}
            {playing ? "Pause" : "Aperçu"}
          </button>

          {/* Expand icon (no caption text) */}
          <span className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity h-9 w-9 rounded-full bg-white/95 text-[#0B3A5B] flex items-center justify-center shadow-lg">
            <Maximize2 className="h-4 w-4" />
          </span>
        </>
      ) : (
        <>
          <img
            src={media.src}
            alt=""
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B3A5B]/40 via-transparent to-transparent" />
          <span className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity h-9 w-9 rounded-full bg-white/95 text-[#0B3A5B] flex items-center justify-center shadow-lg">
            <Maximize2 className="h-4 w-4" />
          </span>
        </>
      )}
    </div>
  );
}

function BentoSkeleton() {
  return (
    <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-7">
      {Array.from({ length: 3 }).map((_, i) => (
        <div
          key={i}
          className={`rounded-[28px] bg-[#F3F5F7] animate-pulse aspect-[3/4] ${
            i === 0 ? "sm:col-span-2 lg:row-span-2 lg:h-auto" : ""
          }`}
        />
      ))}
    </div>
  );
}

function Lightbox({ open, onClose, media }) {
  if (!media) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="gallery-lightbox"
        className="max-w-5xl w-[95vw] p-0 bg-black border-0 rounded-2xl overflow-hidden"
      >
        <DialogTitle className="sr-only">Aperçu vidéo Hingant Clean</DialogTitle>
        <DialogDescription className="sr-only">
          Visualisation en grand format d&apos;une réalisation Hingant Clean
        </DialogDescription>
        <button
          onClick={onClose}
          data-testid="gallery-lightbox-close"
          aria-label="Fermer"
          className="absolute top-3 right-3 z-10 h-9 w-9 rounded-full bg-white/95 hover:bg-white text-[#0B3A5B] flex items-center justify-center"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="w-full max-h-[85vh] flex items-center justify-center bg-black">
          {media.type === "video" ? (
            <video
              src={media.src}
              muted
              autoPlay
              controls
              playsInline
              className="w-full max-h-[85vh] object-contain"
            />
          ) : (
            <img
              src={media.src}
              alt=""
              className="w-full max-h-[85vh] object-contain"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
